import { ChangeEvent, useEffect, useRef, useState } from "react";
import { doc, setDoc } from "firebase/firestore";
import useFirebaseConfig from "shared/hooks/useFirebaseConfig";
import useFirestore from "shared/hooks/useFirestore";
import useGetBranchId from "shared/hooks/useGetBranchId";

interface Student {
    name: string;
    id: string;
    status?: 'active' | 'suspended' | 'graduated';
}

const useStudentAssignListView = (clickedLesson: string) => {
    const { getBranchId } = useGetBranchId();
    const { dbFirestore } = useFirebaseConfig();
    const { readFirestoreWithQuery, readFirestoreOneDoc } = useFirestore();
    const [search, setSearch] = useState("");
    const [lists, setLists] = useState<Student[]>([]);
    const [clickedStudentList, setClickedStudentList] = useState<string[]>([]);
    const [isSucceed, setIsSucced] = useState(false);
    const [isError, setIsError] = useState(false);
    const [loadedLesson, setLoadedLesson] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const lessonRequest = useRef(0);
    const studentRequest = useRef(0);
    const saving = useRef(false);
    const currentLesson = useRef(clickedLesson);
    currentLesson.current = clickedLesson;

    const getStudentList = async () => {
        const request = ++studentRequest.current;
        try {
            const branchId = await getBranchId();
            const response = await readFirestoreWithQuery("User/Auth/Student", {
                field: "branch_id", operator: "==", data: branchId,
            });
            const activeStudents: Student[] = response.docs
                .map((document: any) => ({ ...document.data(), id: document.id }))
                .filter((student: Student) => student.status === 'active' || student.status === undefined);
            activeStudents.sort((a, b) => a.name.localeCompare(b.name));
            if (request === studentRequest.current) setLists(activeStudents);
        } catch (error) {
            if (request === studentRequest.current) setIsError(true);
        }
    };

    useEffect(() => {
        const requests = studentRequest;
        void getStudentList();
        return () => { requests.current++; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const requests = lessonRequest;
        const request = ++requests.current;
        // 이전 반의 선택이나 늦게 도착한 조회 결과가 새 반에 저장되지 않게 합니다.
        setClickedStudentList([]);
        setLoadedLesson("");
        setIsSucced(false);
        setIsError(false);
        const loadAssignment = async () => {
            try {
                const branchId = await getBranchId();
                const response = await readFirestoreOneDoc(`Assign/${branchId}/Lesson`, clickedLesson);
                if (request !== lessonRequest.current) return;
                const students = response.exists() ? response.data().students : [];
                if (students !== undefined && !Array.isArray(students)) {
                    throw new Error("학생 배정 정보가 올바르지 않습니다.");
                }
                setClickedStudentList(Array.from(new Set<string>(
                    (students ?? []).filter((student: unknown): student is string =>
                        typeof student === "string" && student.length > 0),
                )));
                setLoadedLesson(clickedLesson);
            } catch (error) {
                if (request === lessonRequest.current) setIsError(true);
            }
        };
        if (clickedLesson) void loadAssignment();
        return () => { requests.current++; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clickedLesson]);

    const isAssignmentReady = Boolean(clickedLesson) && loadedLesson === clickedLesson;
    const searchedLists = lists.filter((student) => student.name.includes(search));
    const allChecked = searchedLists.length > 0 &&
        searchedLists.every((student) => clickedStudentList.includes(student.id));
    const canEdit = () => isAssignmentReady && !saving.current;

    const onStudentClick = (id: string) => {
        if (!canEdit()) return;
        setClickedStudentList((students) => students.includes(id)
            ? students.filter((student) => student !== id)
            : [...students, id]);
    };

    const onAllStudentClick = () => {
        if (!canEdit() || searchedLists.length === 0) return;
        const visibleIds = new Set(searchedLists.map((student) => student.id));
        setClickedStudentList((students) => searchedLists.every((student) => students.includes(student.id))
            ? students.filter((student) => !visibleIds.has(student))
            : Array.from(new Set([...students, ...Array.from(visibleIds)])));
    };

    const onAssignStudentClick = async () => {
        if (!canEdit()) return;
        const lessonId = clickedLesson;
        const request = lessonRequest.current;
        const students = [...clickedStudentList];
        saving.current = true;
        setIsSaving(true);
        setIsSucced(false);
        setIsError(false);
        try {
            const branchId = await getBranchId();
            if (request !== lessonRequest.current || currentLesson.current !== lessonId) return;
            // 새 반과 기존 반을 동일하게 저장하고 다른 배정 메타데이터는 보존합니다.
            await setDoc(doc(dbFirestore, `Assign/${branchId}/Lesson`, lessonId), { students }, { merge: true });
            if (request === lessonRequest.current) setIsSucced(true);
        } catch (error) {
            if (request === lessonRequest.current) setIsError(true);
        } finally {
            saving.current = false;
            setIsSaving(false);
        }
    };

    return {
        search, searchedLists,
        onRefreshClick: getStudentList,
        onChange: (event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value),
        onStudentClick, clickedStudentList, onAllStudentClick, onAssignStudentClick,
        isSucceed, setIsSucced, isError, setIsError,
        isAssignmentReady, isSaving, allChecked,
    };
};

export default useStudentAssignListView;
